import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ControlPointsService } from '@features/control-points/services/control-points-service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DayOfWeek } from '@shared/models/days-enum';
import { Threshold } from '@shared/models/threshold';
import { TranslatedDayInfoPipe } from '@shared/pipes/translated-days.pipe';
import { FonctionsService } from '@shared/services/utilities/fonctions.service';
import { CurrentStateService } from 'app/core/services/current-state.service';

@Component({
  selector: 'app-trigger-threshold',
  imports: [TranslateModule, ReactiveFormsModule, FormsModule,CommonModule,TranslateModule],
  templateUrl: './trigger-threshold.component.html',
  styleUrl: './trigger-threshold.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TriggerThresholdComponent {
  @Input() controlPointId: number;
  @Input() siteId: number;
  @Input() serialNumber: string;
  triggerForm! : FormGroup;
  initialFormValue: any;
  isFormChanged = false;
  daysOfWeek = [
    { label: '1', name: DayOfWeek.Monday, selected: false },
    { label: '2', name: DayOfWeek.Tuesday, selected: false },
    { label: '3', name: DayOfWeek.Wednesday, selected: false },
    { label: '4', name: DayOfWeek.Thursday, selected: false },
    { label: '5', name: DayOfWeek.Friday, selected: false },
    { label: '6', name: DayOfWeek.Saturday, selected: false },
    { label: '7', name: DayOfWeek.Sunday, selected: false }
  ];

  constructor(private fb: FormBuilder,private controlPointsService: ControlPointsService, private currentStateService: CurrentStateService,
    private fonctionsService: FonctionsService,private translate: TranslateService
  ) {}

  ngOnInit() {
    this.initForm();
  }
  toggleDay(day: any) {
    day.selected = !day.selected;
    this.updateSelectedDaysFormControl();
  }
  updateSelectedDaysFormControl() {
    const selected = this.daysOfWeek
      .filter(d => d.selected)
      .map(d => d.label)
      .join(',');
    this.triggerForm.get('days')?.setValue(selected);
    this.triggerForm.markAllAsTouched();
  }

  initForm() {
    this.triggerForm = this.fb.group({
      triggerLevels: this.fb.array([]), 
      emailsThreshold: this.fb.array([]),
      startHourNotif: [null, [Validators.min(0), Validators.max(23)]],
      startMinuteNotif: [null, [Validators.min(0), Validators.max(59)]],
      endHourNotif: [null, [Validators.min(0), Validators.max(23)]],
      endMinuteNotif: [null, [Validators.min(0), Validators.max(59)]],
      days:[null],
      controlPointId: [this.controlPointId],
      siteId: [this.siteId],
      id: [null],
      redAlertValue: [null],
      yellowAlertValue: [null],
      orangeAlertValue: [null],
    });
    this.addTriggerChannel(4); 
    this.addEmail();
    this.loadThresholdData();
    
    
  }
  private areValuesEqual(currentValue: any, initialValue: any): boolean {
    return JSON.stringify(currentValue) === JSON.stringify(initialValue);
  }
  async loadThresholdData() {
    let threshold = await this.controlPointsService.getThreshold(this.serialNumber, this.siteId, this.controlPointId);
    if (threshold) {
        this.triggerForm.patchValue({
          controlPointId: threshold.controlPointId,
          id: threshold.id,
          siteId: threshold.siteId,
          startHourNotif: threshold.startHourNotif,
          startMinuteNotif: threshold.startMinuteNotif,
          endHourNotif: threshold.endHourNotif,
          endMinuteNotif: threshold.endMinuteNotif,
          days: threshold.days,
          redAlertValue: threshold.redAlertValue,
          yellowAlertValue: threshold.yellowAlertValue,
          orangeAlertValue: threshold.orangeAlertValue
        });
        const selectedArray = (threshold.days || '').split(',');
        this.daysOfWeek.forEach(day => {
          day.selected = selectedArray.includes(day.label);
        });
      //   const emailsArray = this.triggerForm.get('emailsThreshold') as FormArray;
      //   emailsArray.clear();
      //   threshold.emailsThreshold.forEach(email => {
      //     const group = this.createEmailGroup(email);  
      //     emailsArray.push(group);
      // });


        const triggersArray = this.triggerForm.get('triggerLevels') as FormArray;
        triggersArray.clear();
        threshold.triggerLevels.forEach(trigger => {
            const group = this.createTriggerGroup(trigger);  
            triggersArray.push(group);
        });
    }
    
    this.initialFormValue = this.triggerForm.getRawValue();
    this.triggerForm.valueChanges.subscribe(() => {
      this.isFormChanged = !this.areValuesEqual(this.triggerForm.value, this.initialFormValue);      
    });
}
private createEmailGroup(emailData: any = { email: '', isActive: false }) {
  const group = this.fb.group({
    id: [emailData.id],
    thresholdId: [emailData.thresholdId],
    email: [{value: emailData.email,disabled: !emailData.isActive}],
    isActive: [emailData.isActive],
    alarm: [emailData.alarm]
});
const updateEmailValidators = (isActive: boolean) => {
  const emailControl = group.get('email');

  if (isActive) {
      emailControl.setValidators([Validators.required, Validators.email]);
      emailControl.enable();
  } else {
    emailControl.clearValidators();
    emailControl.disable();
  }

  emailControl.updateValueAndValidity();
};

updateEmailValidators(emailData.isActive);

group.get('isActive').valueChanges.subscribe(updateEmailValidators);

return group;
}
get emailsThreshold(): FormArray {
  return this.triggerForm.get('emailsThreshold') as FormArray;
}
  get triggerLevels(): FormArray {
    return this.triggerForm.get('triggerLevels') as FormArray;
  }

  protected addTriggerChannel(channelCount: number) {
    const triggersArray = this.triggerForm.get('triggerLevels') as FormArray;

    for (let i = 0; i < channelCount; i++) {
        const group = this.createTriggerGroup({
            isActive: true, 
            channel: i + 1
        });

        triggersArray.push(group);
    }
}

private createTriggerGroup(triggerData: any) {
  const group = this.fb.group({
      id: [triggerData.id || null],
      isActive: [triggerData.isActive],
      thresholdId: [triggerData.thresholdId || null],
      channel: [triggerData.channel],
      triggerValue: [{ value: triggerData.triggerValue, disabled: !triggerData.isActive }, [Validators.required, Validators.min(0)]],
      alertValue: [{ value: triggerData.alertValue, disabled: !triggerData.isActive }],
      unit: [{value: triggerData.unit || 'mm/s', disabled: !triggerData.isActive}]
  });

  const updateTriggerValidators = (isActive: boolean) => {
      const triggerValueControl = group.get('triggerValue');
      const unitControl = group.get('unit');

      if (isActive) {
          triggerValueControl.setValidators([Validators.required, Validators.min(0)]);
          triggerValueControl.enable();
          unitControl.enable();
      } else {
          triggerValueControl.clearValidators();
          triggerValueControl.disable();
          unitControl.disable();
      }

      triggerValueControl.updateValueAndValidity();
      unitControl.updateValueAndValidity();
  };

  updateTriggerValidators(triggerData.isActive);

  group.get('isActive').valueChanges.subscribe(updateTriggerValidators);

  return group;
}
  protected removeChannel(index: number) {
    this.triggerLevels.removeAt(index);
  }
  async submitForm() {
    if (this.triggerForm.untouched || !this.isFormChanged) {
      return
    }
    this.currentStateService.setShowLoading(true);
    let threshold = this.triggerForm.getRawValue() as Threshold;
    threshold.deviceSerialNumber = this.serialNumber;
    try {
      if(threshold.id == null) {
        await this.controlPointsService.addThreshold(threshold);
      }
      else {
        if(threshold.controlPointId != null){
          await this.controlPointsService.updateThreshold(threshold);
        }
        else if(this.controlPointId != null) {
          threshold.controlPointId = this.controlPointId;
          threshold.siteId = null;
          threshold.id = null;
          threshold.siteId = null;
          threshold.triggerLevels.forEach(trigger => {
            trigger.id = null;
            trigger.thresholdId = null;
          })

          // threshold.emailsThreshold.forEach(email => {
          //   email.id = null;
          //   email.configId = null;
          // })
          await this.controlPointsService.addThreshold(threshold);
        }
        else{
          await this.controlPointsService.updateThreshold(threshold);
        }
        
      }
        
        this.currentStateService.setShowLoading(false);
        this.fonctionsService.launch_toast(true, this.translate.instant('TRIGGER.THRESHOLD_SAVED'));
    } catch (error) {
        this.currentStateService.setShowLoading(false);
        this.fonctionsService.launch_toast(true, this.translate.instant('TRIGGER.THRESHOLD_ERROR'));
    }
  }
  protected addEmail() {
    const email = this.fb.group({
      id: [null],
      isActive: [false],
      email: ['', [Validators.email]],
      alarm:[null]
    });
    this.emailsThreshold.push(email);
  }

  protected removeEmail(index: number) {
    this.emailsThreshold.removeAt(index);
  }
  resetForm(): void {    
    this.triggerForm.setValue(this.initialFormValue);
    this.isFormChanged = false;
  }

  protected getUnitValue(index: number): string {
    return this.triggerLevels.controls[index].get('unit')?.value || 'mm/s';
}

}
